import React, { memo } from 'react';
import { AdminLayout } from '../layout';
import { ErrorDisplay } from '../ui/ErrorDisplay';
import { PageState } from '../common';
import { ProductStatistics, ProductActionBar } from './ProductManagementComponents';
import { ProductTable } from './ProductTableComponents';
import { ProductModals } from './ProductModals';
import { Product } from '../../types/product';
import { OptionType } from '../../types/optionTypes';

interface ProductManagementLayoutProps {
  // Data
  products: Product[];
  loading: boolean;
  error: any;
  searchTerm: string;

  // Statistics
  totalProducts: number;
  averagePrice: string;
  productsWithOptions: number;
  uniqueCategories: number;
  hasValidPrices: boolean;

  // Modals
  showAdd: boolean;
  isEditing: boolean;
  newProduct: Partial<Product>;
  editProduct: Partial<Product> | null;

  // Categories
  mainCategories: any[];
  filteredSubCategories: any[];
  selectedMainCategory: string;
  getMainCategoryName: (id: string | number) => string;
  getSubCategoryName: (id: string | number) => string;

  // Option Types
  optionTypes: OptionType[];

  // Handlers
  onSearchChange: (term: string) => void;
  onShowAdd: () => void;
  onCloseAdd: () => void;
  onAdd: () => void;
  onEdit: (product: Product) => void;
  onCloseEdit: () => void;
  onEditSave: () => void;
  onConfirmDelete: (product: Product) => void;
  onUpdateNewProduct: (updates: Partial<Product>) => void;
  onUpdateEditProduct: (updates: Partial<Product>) => void;
  onMainCategoryChange: (categoryId: string) => void;
  onClearError: () => void;
  onRetry: () => void;

  // Components
  ConfirmComponent: React.ReactNode;
  ToastComponent: React.ReactNode;
}

export const ProductManagementLayout = memo<ProductManagementLayoutProps>(
  ({
    // Data
    products,
    loading,
    error,
    searchTerm,

    // Statistics
    totalProducts,
    averagePrice,
    productsWithOptions,
    uniqueCategories,
    hasValidPrices,

    // Modals
    showAdd,
    isEditing,
    newProduct,
    editProduct,

    // Categories
    mainCategories,
    filteredSubCategories,
    selectedMainCategory,
    getMainCategoryName,
    getSubCategoryName,

    // Option Types
    optionTypes,

    // Handlers
    onSearchChange,
    onShowAdd,
    onCloseAdd,
    onAdd,
    onEdit,
    onCloseEdit,
    onEditSave,
    onConfirmDelete,
    onUpdateNewProduct,
    onUpdateEditProduct,
    onMainCategoryChange,
    onClearError,
    onRetry,

    // Components
    ConfirmComponent,
    ToastComponent,
  }) => {
    return (
      <AdminLayout title="Ürün Yönetimi">
        <PageState
          loading={loading}
          error={error || undefined}
          loadingStateProps={{
            size: 'lg',
            text: 'Ürünler yükleniyor...',
            variant: 'admin',
            skeleton: true,
            skeletonType: 'table',
            skeletonProps: { rows: 8, columns: 6 },
          }}
          errorStateProps={{
            title: 'Ürünler Yükleme Hatası',
            variant: 'admin',
            onRetry: onRetry,
            retryText: 'Yeniden Dene',
          }}
        >
          {/* Error Display */}
          <ErrorDisplay error={error} onDismiss={onClearError} className="mb-6" />

          {/* Statistics Cards */}
          <ProductStatistics
            totalProducts={totalProducts}
            averagePrice={averagePrice}
            productsWithOptions={productsWithOptions}
            uniqueCategories={uniqueCategories}
            hasValidPrices={hasValidPrices}
          />

          {/* Action Bar */}
          <ProductActionBar
            searchTerm={searchTerm}
            onSearchChange={onSearchChange}
            onShowAdd={onShowAdd}
          />

          {/* Products Table */}
          <ProductTable
            products={products}
            onEdit={onEdit}
            onConfirmDelete={onConfirmDelete}
            getMainCategoryName={getMainCategoryName}
            getSubCategoryName={getSubCategoryName}
          />

          {/* Product Modals */}
          <ProductModals
            // Add Modal
            showAdd={showAdd}
            onCloseAdd={onCloseAdd}
            onAdd={onAdd}
            newProduct={newProduct}
            onUpdateNewProduct={onUpdateNewProduct}
            // Edit Modal
            isEditing={isEditing}
            onCloseEdit={onCloseEdit}
            onEditSave={onEditSave}
            editProduct={editProduct}
            onUpdateEditProduct={onUpdateEditProduct}
            // Categories
            mainCategories={mainCategories}
            filteredSubCategories={filteredSubCategories}
            selectedMainCategory={selectedMainCategory}
            onMainCategoryChange={onMainCategoryChange}
            // Option Types
            optionTypes={optionTypes}
          />

          {/* Confirm & Toast Components */}
          {ConfirmComponent}
          {ToastComponent}
        </PageState>
      </AdminLayout>
    );
  }
);

ProductManagementLayout.displayName = 'ProductManagementLayout';
